import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import {
  Moon,
  Link as LinkIcon,
  Tag,
  Bell,
  Share2,
  Calendar,
  UtensilsCrossed,
  Eye,
  EyeOff,
  MapPin,
  Users,
  RefreshCw,
  Infinity,
  UserPlus,
  Mail,
  Lock,
  Clock,
  FileSpreadsheet,
  Plus,
  Send,
  Globe,
  Check,
  MessageSquare,
} from 'lucide-react';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import type { HomepageStrings } from '../../i18n/homepage';

type FeaturesGridStrings = Pick<
  HomepageStrings,
  | 'features_grid_badge'
  | 'features_grid_h2_pre'
  | 'features_grid_h2_highlight'
  | 'features_grid_body'
  | 'features_grid_items'
>;

const EN_DEFAULTS: FeaturesGridStrings = {
  features_grid_badge: 'More Features',
  features_grid_h2_pre: 'Powerful tools for ',
  features_grid_h2_highlight: 'every detail',
  features_grid_body: 'Guest management, tracking, reminders and more. All included, no extras.',
  features_grid_items: [
    { title: 'Day & Evening Guests', description: "One invite handles both. Evening guests only see what's relevant to them." },
    { title: 'Your Link, Your Way', description: "Customise your link, then track exactly who's viewed and responded." },
    { title: 'Custom Questions', description: 'Ask guests anything — add your own questions to the RSVP form.' },
    { title: 'RSVP Reminders', description: "Nudge guests who haven't responded yet — one tap from your dashboard." },
    { title: 'Share via Link, WhatsApp & QR', description: "Send it however your guests will actually see it. No app needed." },
    { title: 'Save the Date + Invitation', description: 'Two invite types included. Send your Save the Date first, then your full invitation.' },
    { title: 'Dietary Requirements', description: 'Collect dietary needs as part of the RSVP.' },
    { title: 'Guest Grouping & Tagging', description: 'Organise guests into groups and manage them by tag.' },
    { title: 'View Tracking', description: "See who's opened their invite and who hasn't looked yet." },
    { title: 'Multiple Venues', description: 'Ceremony in one place, reception in another? No problem.' },
    { title: 'Invite Collaborators', description: 'Invite your partner, planner, or anyone else to help manage your invites.' },
    { title: 'Live Invites', description: 'Update your invite anytime. Guests always see the latest — no need to resend.' },
    { title: 'Unlimited Guests', description: 'Invite as many people as you want. No caps, no extra charges.' },
    { title: 'Plus-One Controls', description: 'Set plus-one limits per guest. Collect names and dietary info for each additional guest.' },
    { title: 'Email Sending', description: 'Send invite links directly via email from your dashboard. No copy-pasting needed.' },
    { title: 'Show & Hide by Tag', description: 'Control which sections each guest group sees. Evening guests skip the ceremony details.' },
    { title: 'Public & Private Invites', description: 'Choose between a single shareable link or individual tracked links for each guest.' },
    { title: 'Schedule & Timeline', description: 'Add a full timeline of the day so guests know exactly what to expect and when.' },
    { title: 'CSV Import & Export', description: 'Import your guest list from a spreadsheet or export your data anytime.' },
    { title: 'Multi-Language Invites', description: 'Write your invite in as many languages as you need. Guests can select their language when they open it.' },
  ],
};

const FEATURE_META: { icon: ReactNode; comingSoon?: boolean; visual?: ReactNode; weddingOnly?: boolean }[] = [
  {
    icon: <Moon className="w-5 h-5" />,
    weddingOnly: true,
    visual: (
      <div className="w-full max-w-[260px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-border">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">E</div>
          <span className="text-xs text-text-primary font-medium flex-1">Emily Watson</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/15 text-text-primary">Day</span>
        </div>
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-border">
          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary">T</div>
          <span className="text-xs text-text-primary font-medium flex-1">Tom Brown</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-pink-500/15 text-text-primary">Evening</span>
        </div>
        <div className="px-3.5 py-2 bg-background-card flex items-center justify-between">
          <span className="text-[10px] text-text-muted">Ceremony</span>
          <div className="flex items-center gap-2">
            <Eye size={10} className="text-primary" />
            <EyeOff size={10} className="text-text-muted" />
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <LinkIcon className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[260px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-3.5 py-3 flex items-center gap-2">
          <LinkIcon size={12} className="text-text-muted flex-shrink-0" />
          <span className="text-xs text-text-secondary truncate">
            wedsy.co/i/
            <span className="text-primary font-semibold">emma-and-james</span>
          </span>
        </div>
      </div>
    ),
  },
  {
    icon: <MessageSquare className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden px-3 py-3 space-y-2">
        <div className="space-y-1.5">
          <p className="text-[9px] font-medium text-text-secondary uppercase tracking-wide">Meal choice</p>
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary/10 text-primary"><Check size={10} />Chicken</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-background-hover text-text-secondary">Beef</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-background-hover text-text-secondary">Vegetarian</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="text-[9px] font-medium text-text-secondary uppercase tracking-wide">Any other notes?</p>
          <div className="px-2 py-1.5 rounded-md border border-border bg-background-surface text-[10px] text-text-muted">Gluten free if possible...</div>
        </div>
      </div>
    ),
  },
  {
    icon: <Bell className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[260px] mx-auto flex flex-col gap-2">
        <div className="bg-background rounded-lg border border-border shadow-sm overflow-hidden px-3.5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <Bell size={16} className="text-amber-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold text-text-primary">5 guests haven't replied</p>
            <p className="text-[10px] text-text-muted">RSVP deadline in 3 days</p>
          </div>
        </div>
        <div className="bg-primary rounded-lg px-3.5 py-2 flex items-center justify-center gap-2">
          <Send size={12} className="text-white" />
          <span className="text-[11px] font-semibold text-white">Send Reminder</span>
        </div>
      </div>
    ),
  },
  {
    icon: <Share2 className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2.5 px-3 py-2 border-b border-border">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center"><LinkIcon size={12} className="text-primary" /></div>
          <span className="text-[11px] font-medium text-text-primary">Copy Link</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2 border-b border-border">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center"><Send size={12} className="text-primary" /></div>
          <span className="text-[11px] font-medium text-text-primary">WhatsApp</span>
        </div>
        <div className="flex items-center gap-2.5 px-3 py-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center"><Mail size={12} className="text-primary" /></div>
          <span className="text-[11px] font-medium text-text-primary">Email</span>
        </div>
      </div>
    ),
  },
  {
    icon: <Calendar className="w-5 h-5" />,
    weddingOnly: true,
    visual: (
      <div className="w-full max-w-[240px] mx-auto relative h-[70px]">
        {/* Save the Date card */}
        <div className="absolute left-2 top-0 w-[55%] bg-background rounded-lg border border-border shadow-sm overflow-hidden -rotate-2">
          <div className="px-3 py-1.5 border-b border-border bg-primary/5">
            <span className="text-[8px] font-semibold text-primary">Save the Date</span>
          </div>
          <div className="px-3 py-2">
            <p className="text-[9px] text-text-primary font-medium">Emma & James</p>
            <p className="text-[8px] text-text-muted">14th June 2025</p>
          </div>
        </div>
        {/* Full Invite card */}
        <div className="absolute right-2 top-2 w-[55%] bg-background rounded-lg border border-border shadow-sm overflow-hidden rotate-1">
          <div className="px-3 py-1.5 border-b border-border bg-primary/5">
            <span className="text-[8px] font-semibold text-primary">Full Invite</span>
          </div>
          <div className="px-3 py-2">
            <p className="text-[9px] text-text-primary font-medium">Emma & James</p>
            <p className="text-[8px] text-text-muted">RSVP by 1st May</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <UtensilsCrossed className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden px-3 py-3">
        <div className="flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-primary/10 text-primary"><Check size={10} />Vegetarian</span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-background-hover text-text-secondary">Vegan</span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-background-hover text-text-secondary">Pescatarian</span>

        </div>
      </div>
    ),
  },
  {
    icon: <Tag className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[260px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-3 py-2.5 flex flex-wrap items-center gap-1.5">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-pink-500/15 text-text-primary">Evening</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-text-primary">Friends</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/15 text-text-primary">Table 3</span>
        </div>
        <div className="px-3 py-2 border-t border-border flex items-center gap-1.5">
          <Plus size={11} className="text-primary" />
          <span className="text-[11px] text-primary font-medium">Add tag</span>
        </div>
      </div>
    ),
  },
  {
    icon: <Eye className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-border">
          <span className="text-[11px] text-text-primary font-medium flex-1">Emily Watson</span>
          <div className="inline-flex items-center gap-1 text-text-secondary"><Eye size={12} /><span className="text-[11px] font-medium">5</span></div>
        </div>
        <div className="flex items-center gap-2.5 px-3.5 py-2.5">
          <span className="text-[11px] text-text-primary font-medium flex-1">Tom Brown</span>
          <div className="inline-flex items-center gap-1 text-text-secondary"><Eye size={12} /><span className="text-[11px] font-medium">0</span></div>
        </div>
      </div>
    ),
  },
  {
    icon: <MapPin className="w-5 h-5" />,
    weddingOnly: true,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <MapPin size={10} className="text-primary flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[9px] text-text-muted">Ceremony</p>
            <p className="text-[10px] text-text-primary font-medium">St Mary's Church</p>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2">
          <MapPin size={10} className="text-primary flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[9px] text-text-muted">Reception</p>
            <p className="text-[10px] text-text-primary font-medium">The Barn</p>
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <Users className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-3 py-2.5 flex flex-col items-center gap-2">
          <div className="flex items-center -space-x-2">
            <div className="w-7 h-7 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center text-[9px] font-medium text-primary">E</div>
            <div className="w-7 h-7 rounded-full bg-pink-500/20 border-2 border-background flex items-center justify-center text-[9px] font-medium text-pink-600">J</div>
            <div className="w-7 h-7 rounded-full bg-amber-500/20 border-2 border-background flex items-center justify-center text-[9px] font-medium text-amber-600">S</div>
            <div className="w-7 h-7 rounded-full border-2 border-dashed border-border bg-background-card flex items-center justify-center">
              <Plus size={10} className="text-text-muted" />
            </div>
          </div>
          <span className="text-[9px] text-text-secondary">3 collaborators</span>
        </div>
      </div>
    ),
  },
  {
    icon: <RefreshCw className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          <span className="text-[10px] text-text-primary font-medium flex-1">Wedding Invitation</span>
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-medium bg-success/10 text-success">
            <span className="w-1.5 h-1.5 rounded-full bg-success" />
            Live
          </span>
        </div>
        <div className="px-3 py-1.5 bg-background-card flex items-center gap-2">
          <RefreshCw size={8} className="text-text-muted" />
          <span className="text-[8px] text-text-muted">Updated 2m ago</span>
        </div>
      </div>
    ),
  },
  {
    icon: <Infinity className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto grid grid-cols-3 gap-1.5">
        <div className="bg-background rounded-lg border border-border p-2">
          <div className="text-[14px] font-bold text-text-primary">72</div>
          <div className="text-[9px] text-success font-medium">Attending</div>
        </div>
        <div className="bg-background rounded-lg border border-border p-2">
          <div className="text-[14px] font-bold text-text-primary">15</div>
          <div className="text-[9px] text-text-muted font-medium">Awaiting</div>
        </div>
        <div className="bg-background rounded-lg border border-border p-2">
          <div className="text-[14px] font-bold text-text-primary">5</div>
          <div className="text-[9px] text-amber-500 font-medium">Maybe</div>
        </div>
      </div>
    ),
  },
  {
    icon: <UserPlus className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-[10px] text-text-primary font-medium">Allow plus ones</span>
          <div className="w-7 h-4 rounded-full bg-primary relative"><div className="absolute right-0.5 top-0.5 w-3 h-3 rounded-full bg-white" /></div>
        </div>
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-[10px] text-text-secondary">Max per guest</span>
          <span className="px-2 py-0.5 rounded bg-background-card border border-border text-[9px] font-medium text-text-primary">1</span>
        </div>
      </div>
    ),
  },
  {
    icon: <Mail className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-3 py-1.5 border-b border-border flex items-center gap-1.5">
          <Mail size={9} className="text-text-muted flex-shrink-0" />
          <span className="text-[9px] text-text-primary">You're Invited</span>
        </div>
        <div className="px-3 py-1.5 flex items-center justify-between">
          <span className="text-[8px] text-text-muted">12 guests selected</span>
          <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary text-white text-[9px] font-medium">
            <Send size={8} />
            Send All
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <EyeOff className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-3.5 py-2 border-b border-border">
          <span className="text-[10px] text-text-secondary font-medium">Visible to</span>
        </div>
        <div className="px-3 py-2.5 flex flex-wrap gap-1.5">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-500/15 text-text-primary">Day Guest</span>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/15 text-text-primary">Family</span>
        </div>
      </div>
    ),
  },
  {
    icon: <Lock className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[200px] mx-auto">
        <div className="flex gap-1 bg-background rounded-lg p-1 border border-border shadow-sm">
          <div className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-white">
            <Globe size={13} />
            Public
          </div>
          <div className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-text-secondary">
            <Lock size={13} />
            Private
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <Clock className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-3 py-2.5 flex gap-3">
          {/* Vertical line with dots */}
          <div className="flex flex-col items-center gap-0">
            <div className="w-2 h-2 rounded-full bg-primary" />
            <div className="w-px flex-1 bg-border" />
            <div className="w-2 h-2 rounded-full bg-primary" />
            <div className="w-px flex-1 bg-border" />
            <div className="w-2 h-2 rounded-full bg-primary" />
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <div>
              <p className="text-[8px] text-text-muted">1:00 PM</p>
              <p className="text-[10px] text-text-primary font-medium">Ceremony</p>
            </div>
            <div>
              <p className="text-[8px] text-text-muted">2:30 PM</p>
              <p className="text-[10px] text-text-primary font-medium">Drinks</p>
            </div>
            <div>
              <p className="text-[8px] text-text-muted">4:00 PM</p>
              <p className="text-[10px] text-text-primary font-medium">Dinner</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: <FileSpreadsheet className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        {/* Mini spreadsheet header */}
        <div className="grid grid-cols-3 border-b border-border">
          <div className="px-2 py-1 text-[8px] font-semibold text-text-primary border-r border-border bg-background-card">Name</div>
          <div className="px-2 py-1 text-[8px] font-semibold text-text-primary border-r border-border bg-background-card">Email</div>
          <div className="px-2 py-1 text-[8px] font-semibold text-text-primary bg-background-card">Tag</div>
        </div>
        {/* Data rows */}
        <div className="grid grid-cols-3 border-b border-border">
          <div className="px-2 py-1 text-[8px] text-text-secondary border-r border-border">Emily W.</div>
          <div className="px-2 py-1 text-[8px] text-text-secondary border-r border-border truncate">emily@...</div>
          <div className="px-2 py-1 text-[8px] text-text-secondary">Day</div>
        </div>
        <div className="grid grid-cols-3 border-b border-border">
          <div className="px-2 py-1 text-[8px] text-text-secondary border-r border-border">Tom B.</div>
          <div className="px-2 py-1 text-[8px] text-text-secondary border-r border-border truncate">tom@...</div>
          <div className="px-2 py-1 text-[8px] text-text-secondary">Evening</div>
        </div>
        {/* Action buttons */}
        <div className="px-2 py-1.5 flex items-center gap-2">
          <div className="px-2 py-0.5 rounded text-[8px] font-medium bg-primary/10 text-primary">Import</div>
          <div className="px-2 py-0.5 rounded text-[8px] font-medium bg-background-hover text-text-secondary">Export</div>
        </div>
      </div>
    ),
  },
  {
    icon: <Globe className="w-5 h-5" />,
    visual: (
      <div className="w-full max-w-[240px] mx-auto bg-background rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-background-card">
          <Globe size={11} className="text-text-secondary" />
          <span className="text-[9px] font-semibold text-text-primary">Languages</span>
        </div>
        {[
          { flag: '🇬🇧', label: 'English', active: true },
          { flag: '🇪🇸', label: 'Spanish', active: true, dot: true },
          { flag: '🇫🇷', label: 'French', active: false },
          { flag: '🇩🇪', label: 'German', active: false },
        ].map(lang => (
          <div key={lang.label} className={`flex items-center gap-2 px-3 py-1.5 border-b border-border last:border-0 ${lang.active ? '' : 'opacity-40'}`}>
            <span className="text-[11px]">{lang.flag}</span>
            <span className="text-[9px] text-text-primary flex-1">{lang.label}</span>
            {lang.dot && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
          </div>
        ))}
      </div>
    ),
  },
];

export function FeaturesGridSection({
  showWeddingFeatures = true,
  strings,
}: {
  showWeddingFeatures?: boolean;
  strings?: FeaturesGridStrings;
}) {
  const s = strings ?? EN_DEFAULTS;
  const allMeta = showWeddingFeatures ? FEATURE_META : FEATURE_META.filter(f => !f.weddingOnly);
  const allItems = showWeddingFeatures ? s.features_grid_items : s.features_grid_items.filter((_, i) => !FEATURE_META[i]?.weddingOnly);

  return (
    <section id="features" className="py-20 md:py-32 bg-background-card">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            {s.features_grid_badge}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
            style={displayFont}
          >
            {s.features_grid_h2_pre}<span className="text-primary">{s.features_grid_h2_highlight}</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto"
          >
            {s.features_grid_body}
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {allMeta.map((meta, index) => {
            const item = allItems[index];
            if (!item) return null;
            return (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -4, transition: { duration: 0.2 } }}
                className="bg-background rounded-xl border border-border hover:shadow-md transition-shadow overflow-hidden"
              >
                {meta.visual && (
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 h-44 px-4 py-5 flex items-center justify-center">
                    {meta.visual}
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 text-primary">
                      {meta.icon}
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-text-primary mb-1 flex items-center gap-2">
                        {item.title}
                        {meta.comingSoon && (
                          <span className="text-[10px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            Coming Soon
                          </span>
                        )}
                      </h3>
                      <p className="text-xs sm:text-sm text-text-secondary leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
